import { type ClientFeatureType } from 'shared/api/clients'
import { type InternalFeatureType } from 'shared/hooks/useHasFeatureAccess'

export const CLIENT_ID_QUERY_PARAM = 'clientId'

const HOME_BASE_ROUTE = '/'
const AUTH_BASE_ROUTE = '/auth'
const REPORTING_SOLUTIONS_BASE_ROUTE = '/reporting-solutions'
const CONNECTORS_BASE_ROUTE = '/connectors'
const ATTRIBUTION_MODELS_BASE_ROUTE = '/attribution-models'
const DATA_SOURCES_BASE_ROUTE = '/data-sources'
const BUDGET_OPTIMISER_BASE_ROUTE = '/budget-optimiser'
const MIMIR_BASE_ROUTE = '/mimir'
const WORKSPACE_BASE_ROUTE = '/workspace'
const ENHANCED_SIGNALS_BASE_ROUTE = '/bidding-signals'
const CUSTOM_GROUPINGS_BASE_ROUTE = '/custom-groupings'
const CENTRAL_APP_BASE_ROUTE = 'https://precis-central.appspot.com'
const ACCOUNT_BASE_ROUTE = '/account'
const ERROR500_BASE_ROUTE = '/500'
const CREATIVE_REPORTING_BASE_ROUTE = '/creative-reporting'
const CREATIVE_ASSETS_BASE_ROUTE = '/creative-assets'
const RECOMMENDATIONS_BASE_ROUTE = '/recommendations'
const COPILOT_BASE_ROUTE = '/ai'
/**
 * A registry for all app routes
 */
export const APP_ROUTES = {
  home: {
    basePage: HOME_BASE_ROUTE
  },
  recommendations: {
    basePage: RECOMMENDATIONS_BASE_ROUTE,
    filterBasePage: ({ severity }: { severity: string }): string => {
      const filters = [
        {
          parameter: {
            value: 'severity',
            label: 'Severity'
          },
          operator: 'is',
          data: {
            value: severity,
            label: severity
          }
        }
      ]
      return `${RECOMMENDATIONS_BASE_ROUTE}/?filters=${JSON.stringify(filters)}`
    }
  },
  auth: {
    signUpPage: `${AUTH_BASE_ROUTE}/signup`,
    signInPage: `${AUTH_BASE_ROUTE}/signin`,
    gcpMarketplaceSignUpPage: `${AUTH_BASE_ROUTE}/gcp-marketplace/signup`,
    gcpMarketplaceLoginPage: `${AUTH_BASE_ROUTE}/gcp-marketplace/login`,
    forgotPasswordPage: `${AUTH_BASE_ROUTE}/forgot-password`, // NOSONAR(S2068)
    createNewPasswordPage: `${AUTH_BASE_ROUTE}/create-new-password`, // NOSONAR(S2068)
    verifyEmailPage: `${AUTH_BASE_ROUTE}/verify-email`,
    // Identity Platform's project-wide email Action URL must point at this path.
    // Changing it here means changing it in the Firebase console too.
    actionPage: `${AUTH_BASE_ROUTE}/action`
  },
  account: {
    basePage: ACCOUNT_BASE_ROUTE,
    deleteAccountPage: `${ACCOUNT_BASE_ROUTE}/delete-account`
  },
  reportingSolutions: {
    basePage: REPORTING_SOLUTIONS_BASE_ROUTE,
    createConfigPage: `${REPORTING_SOLUTIONS_BASE_ROUTE}/new`,
    errorPage: `${REPORTING_SOLUTIONS_BASE_ROUTE}?q=statusEnum:error`,
    editConfigPage: function ({ configId }: { configId: string }): string {
      return `${REPORTING_SOLUTIONS_BASE_ROUTE}/${configId}`
    },
    dashboardPage: function ({ configId }: { configId: string }): string {
      return `${REPORTING_SOLUTIONS_BASE_ROUTE}/${configId}/dashboard`
    }
  },
  connectors: {
    basePage: CONNECTORS_BASE_ROUTE,
    createConfigPage: `${CONNECTORS_BASE_ROUTE}/new`,
    errorPage: `${CONNECTORS_BASE_ROUTE}?q=statusEnum:error`,
    editConfigPage: function ({ configId }: { configId: string }): string {
      return `${CONNECTORS_BASE_ROUTE}/${configId}`
    }
  },
  attributionModels: {
    basePage: ATTRIBUTION_MODELS_BASE_ROUTE,
    createConfigPage: `${ATTRIBUTION_MODELS_BASE_ROUTE}/new`,
    errorPage: `${ATTRIBUTION_MODELS_BASE_ROUTE}?q=statusEnum:error`,
    editConfigPage: function ({ configId }: { configId: string }): string {
      return `${ATTRIBUTION_MODELS_BASE_ROUTE}/${configId}/edit`
    },
    dashboardPage: function ({ configId }: { configId: string }): string {
      return `${ATTRIBUTION_MODELS_BASE_ROUTE}/${configId}/dashboard`
    }
  },
  dataSources: {
    basePage: DATA_SOURCES_BASE_ROUTE,
    disconnected: `${DATA_SOURCES_BASE_ROUTE}?status=disconnected`
  },
  budgetOptimiser: {
    basePage: BUDGET_OPTIMISER_BASE_ROUTE,
    createConfigPage: `${BUDGET_OPTIMISER_BASE_ROUTE}/new`,
    errorPage: `${BUDGET_OPTIMISER_BASE_ROUTE}?q=statusEnum:error`,
    editConfigPage: function ({ configId }: { configId: string }): string {
      return `${BUDGET_OPTIMISER_BASE_ROUTE}/${configId}`
    },
    dashboardPage: function ({ configId }: { configId: string }): string {
      return `${BUDGET_OPTIMISER_BASE_ROUTE}/${configId}/dashboard`
    }
  },
  mimir: {
    basePage: MIMIR_BASE_ROUTE,
    chatPage: `${MIMIR_BASE_ROUTE}/chat`,
    searchPage: `${MIMIR_BASE_ROUTE}/search`
  },
  copilot: {
    basePage: COPILOT_BASE_ROUTE,
    chatPage: `${COPILOT_BASE_ROUTE}/chat`,
    chatDetailPage: function ({ chatId }: { chatId: string }): string {
      return `${COPILOT_BASE_ROUTE}/chat/${chatId}`
    },
    settingsPage: `${COPILOT_BASE_ROUTE}/settings`,
    settingsGeneralPage: `${COPILOT_BASE_ROUTE}/settings/general`,
    settingsKnowledgeSourcesPage: `${COPILOT_BASE_ROUTE}/settings/knowledge-sources`,
    settingsSkillsPage: `${COPILOT_BASE_ROUTE}/settings/skills`,
    settingsMcpPage: `${COPILOT_BASE_ROUTE}/settings/mcp`,
    settingsUsagePage: `${COPILOT_BASE_ROUTE}/settings/usage`
  },
  workspace: {
    basePage: WORKSPACE_BASE_ROUTE,
    setupPage: `${WORKSPACE_BASE_ROUTE}/setup`,
    listPage: `${WORKSPACE_BASE_ROUTE}/list`,
    noWorkspacePage: `${WORKSPACE_BASE_ROUTE}/noworkspace`,
    joinWorkspacePage: `${WORKSPACE_BASE_ROUTE}/join-workspace`,
    membersPage: `${WORKSPACE_BASE_ROUTE}/members`,
    notificationSettingsPage: `${WORKSPACE_BASE_ROUTE}/notification-settings`
  },
  enhancedSignals: {
    basePage: ENHANCED_SIGNALS_BASE_ROUTE,
    createConfigPage: `${ENHANCED_SIGNALS_BASE_ROUTE}/new`,
    editConfigPage: function ({ configId }: { configId: string }): string {
      return `${ENHANCED_SIGNALS_BASE_ROUTE}/${configId}/edit`
    },
    dashboardPage: function ({ configId }: { configId: string }): string {
      return `${ENHANCED_SIGNALS_BASE_ROUTE}/${configId}/dashboard`
    }
  },
  customGroupings: {
    basePage: CUSTOM_GROUPINGS_BASE_ROUTE,
    editConfigPage: function ({ channelGroupingId }: { channelGroupingId: string }): string {
      return `${CUSTOM_GROUPINGS_BASE_ROUTE}/${channelGroupingId}`
    },
    usedInTabPage: function ({ channelGroupingId }: { channelGroupingId: string }): string {
      return `${CUSTOM_GROUPINGS_BASE_ROUTE}/${channelGroupingId}/used-in`
    },
    dataSourcesPage: function ({ channelGroupingId }: { channelGroupingId: string }): string {
      return `${CUSTOM_GROUPINGS_BASE_ROUTE}/${channelGroupingId}/data-sources`
    },
    inspectResultsPage: function ({
      channelGroupingId,
      dataSource
    }: {
      channelGroupingId: string
      dataSource: string
    }): string {
      return `${CUSTOM_GROUPINGS_BASE_ROUTE}/${channelGroupingId}/data-sources/${dataSource}/inspect-results`
    },
    channelsPage: function ({
      channelGroupingId,
      dataSource
    }: {
      channelGroupingId: string
      dataSource: string
    }): string {
      return `${CUSTOM_GROUPINGS_BASE_ROUTE}/${channelGroupingId}/data-sources/${dataSource}/channels`
    }
  },
  centralApp: {
    basePage: CENTRAL_APP_BASE_ROUTE,
    budgetOptimiser: {
      createConfigPage: `${CENTRAL_APP_BASE_ROUTE}/budget-optimiser/new`,
      editConfigPage: function ({ configId }: { configId: string }): string {
        return `${CENTRAL_APP_BASE_ROUTE}/budget-optimiser/${configId}`
      }
    },
    integratedAttribution: {
      createConfigPage: `${CENTRAL_APP_BASE_ROUTE}/integrated-attribution/new`
    },
    attributionModels: {
      createConfigPage: `${CENTRAL_APP_BASE_ROUTE}/attribution-models/new`,
      editConfigPage: function ({ configId }: { configId: string }): string {
        return `${CENTRAL_APP_BASE_ROUTE}/attribution-models/${configId}`
      }
    },
    credentialsPage: `${CENTRAL_APP_BASE_ROUTE}/credentials`
  },
  error500: {
    basePage: ERROR500_BASE_ROUTE
  },
  creativeReporting: {
    basePage: CREATIVE_REPORTING_BASE_ROUTE,
    createDashboardPage: `${CREATIVE_REPORTING_BASE_ROUTE}/new`,
    dashboardPage: function ({ dashboardId }: { dashboardId: string }): string {
      return `${CREATIVE_REPORTING_BASE_ROUTE}/${dashboardId}/dashboard`
    }
  },
  creativeAssets: {
    basePage: CREATIVE_ASSETS_BASE_ROUTE,
    mainProjectPage: function ({ projectId, tab }: { projectId: string; tab?: string }): string {
      return `${CREATIVE_ASSETS_BASE_ROUTE}/${projectId}/${tab ?? ''}`
    },
    projectAssetPage: function ({ projectId, assetId }: { projectId: string; assetId: string }): string {
      return `${CREATIVE_ASSETS_BASE_ROUTE}/${projectId}/ad-library/${assetId}/details`
    }
  }
} as const

export const CLIENT_FEATURE_TO_ROUTE_MAP: Record<ClientFeatureType, string | string[]> = {
  reportingSolutions: [APP_ROUTES.reportingSolutions.basePage, APP_ROUTES.creativeReporting.basePage],
  connectors: APP_ROUTES.connectors.basePage,
  budgetOptimiser: APP_ROUTES.budgetOptimiser.basePage,
  attributionModels: APP_ROUTES.attributionModels.basePage,
  enhancedSignals: APP_ROUTES.enhancedSignals.basePage,
  creativeAssets: APP_ROUTES.creativeAssets.basePage
}

export const INTERNAL_FEATURE_TO_ROUTE_MAP: Record<InternalFeatureType, string> = {
  workspace_setup: APP_ROUTES.workspace.setupPage,
  mimir: APP_ROUTES.mimir.basePage
}

export const getFeatureIdFromRoute = <T extends string | number | symbol>(
  currentRoute: string | undefined,
  featureRouteMap: Record<T, string | string[]>
): T | undefined => {
  if (currentRoute == null) return

  for (const feature in featureRouteMap) {
    if (Array.isArray(featureRouteMap[feature])) {
      if (featureRouteMap[feature].some((route) => currentRoute.includes(route))) {
        return feature
      }
    } else if (currentRoute.includes(featureRouteMap[feature])) {
      return feature
    }
  }
}
